/**
 * 対戦設定（対戦モード・自分の手番・CPU の難易度）の定義と解析。
 * DOM 操作・localStorage へのアクセスは行わない純粋モジュールとして、
 * Node（Vitest）上で単体テストできるようにする。
 */
import { CPU_DIFFICULTIES, type CpuDifficulty } from '../game/cpu';
import type { Player } from '../game/types';

/** 対戦モード: pvp = 2 人対戦（ローカル）、cpu = CPU 対戦 */
export type GameMode = 'pvp' | 'cpu';

export const GAME_MODES: readonly GameMode[] = ['pvp', 'cpu'];

/** localStorage に保存するキー */
export const SETTINGS_STORAGE_KEY = 'gomoku3d-settings';

/** 対戦設定 */
export interface MatchSettings {
  mode: GameMode;
  /** CPU 対戦で自分が選ぶ手番（pvp では無視される） */
  playerSide: Player;
  /** CPU の難易度（cpu モードでのみ有効） */
  difficulty: CpuDifficulty;
}

/** デフォルト設定 */
export const DEFAULT_SETTINGS: MatchSettings = {
  mode: 'pvp',
  playerSide: 'first',
  difficulty: 'normal',
};

/**
 * 保存済み設定を解析する。
 * 不正な値・欠損したフィールド・壊れた JSON はデフォルト値で補完する。
 * @param raw localStorage から読んだ文字列（未保存なら null）
 */
export function parseSettings(raw: string | null): MatchSettings {
  if (raw === null) return { ...DEFAULT_SETTINGS };
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return { ...DEFAULT_SETTINGS };
    const obj = parsed as Record<string, unknown>;
    return {
      mode: normalizeMode(obj.mode),
      playerSide: normalizeSide(obj.playerSide),
      difficulty: normalizeDifficulty(obj.difficulty),
    };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
}

function normalizeMode(value: unknown): GameMode {
  return (GAME_MODES as readonly string[]).includes(value as string)
    ? (value as GameMode)
    : DEFAULT_SETTINGS.mode;
}

function normalizeSide(value: unknown): Player {
  return value === 'first' || value === 'second' ? value : DEFAULT_SETTINGS.playerSide;
}

function normalizeDifficulty(value: unknown): CpuDifficulty {
  return (CPU_DIFFICULTIES as readonly string[]).includes(value as string)
    ? (value as CpuDifficulty)
    : DEFAULT_SETTINGS.difficulty;
}

/** 設定を localStorage 用文字列にシリアライズする */
export function serializeSettings(settings: MatchSettings): string {
  return JSON.stringify(settings);
}
